import { ScoreDefinitionDTO } from 'business/models/Models';
import EAutoScoreType from 'enums/EAutoScoreType';
import { scorePercentagesEnum } from './constants';

export const getScoreToolTip = (wellDropScore, score: ScoreDefinitionDTO) => {
  if (score) {
    const toolTipParts = [];

    const scoreType = score.isAutoScore ? score.scoreGroupName : 'Manual Score';
    const scoreValue =
      wellDropScore.autoScoreType === EAutoScoreType.FRAP
        ? wellDropScore?.autoScoreValue?.toFixed(2)
        : score.scoreName;
    toolTipParts.push(`${scoreType}: ${scoreValue}`);

    if (wellDropScore.scoreDetails?.length) {
      wellDropScore.scoreDetails.forEach((scoreDetail) => {
        const percentage = Math.round(parseFloat(scoreDetail.value) * 100);
        const character = scorePercentagesEnum.getCharacter(scoreDetail.item);
        toolTipParts.push(`${character}: ${percentage}%`);
      });
    }

    if (wellDropScore.morphologyDetails?.length) {
      toolTipParts.push('\nMorphology:');
      wellDropScore.morphologyDetails.forEach((morphologyDetail) => {
        const percentage = Math.round(parseFloat(morphologyDetail.value) * 100);
        const character = scorePercentagesEnum.getCharacter(morphologyDetail.item);
        toolTipParts.push(`${character}: ${percentage}%`);
      });
    }

    return toolTipParts.join('\n');
  } else if (wellDropScore.isUVIndicator) {
    switch (wellDropScore.scoreName) {
      case '+':
        return `${wellDropScore.scoreGroupName}: Positive Hit`;
      case '-':
        return `${wellDropScore.scoreGroupName}: Negative Hit`;
      default:
        return `${wellDropScore.scoreGroupName}: ${wellDropScore.scoreName}`;
    }
  }
};

export const getScoreSizes = ({
  count,
  hoveredIndex,
  enableHoverZoom,
  baseSize,
  hoverBaseSize,
  hoveredSize,
}: {
  count: number;
  hoveredIndex: number | null;
  enableHoverZoom: boolean;
  baseSize: number;
  hoverBaseSize: number;
  hoveredSize: number;
}): number[] => {
  const isHoverActive = enableHoverZoom && hoveredIndex !== null;
  return Array.from({ length: count }, (_, i) =>
    !isHoverActive ? baseSize : i === hoveredIndex ? hoveredSize : hoverBaseSize,
  );
};

export const computeShiftsAndTotalShift = (sizes: number[], baseSize: number) => {
  const shiftForIndex: number[] = new Array(sizes.length);
  let runningExtra = 0;
  for (let i = sizes.length - 1; i >= 0; i--) {
    shiftForIndex[i] = runningExtra;
    runningExtra += sizes[i] - baseSize;
  }
  return { shiftForIndex, totalShift: runningExtra };
};

export const computeXPositions = (
  items: { column: number }[],
  sizes: number[],
  shiftForIndex: number[],
  originX: number,
  baseSize: number,
): number[] =>
  items.map(
    (item, i) => originX + baseSize * (item.column + 1) - shiftForIndex[i] - sizes[i],
  );

export const sortScoreItems = <T extends { isAutoScore: boolean; sortName?: string }>(
  items: T[],
): T[] =>
  [...items].sort((a, b) => {
    if (a.isAutoScore !== b.isAutoScore) {
      return a.isAutoScore ? 1 : -1;
    }
    return (a.sortName || '').localeCompare(b.sortName || '');
  });

export const findHoveredScoreIndex = (
  pos: { x: number; y: number },
  items: { column: number }[],
  ctx: {
    hoveredIndex: number | null;
    xPositions: number[];
    sizePositions: number[];
    originX: number;
    originY: number;
    baseSize: number;
  },
): number | null => {
  const { hoveredIndex, xPositions, sizePositions, originX, originY, baseSize } = ctx;

  if (
    hoveredIndex !== null &&
    pos.x >= xPositions[hoveredIndex] &&
    pos.x <= xPositions[hoveredIndex] + sizePositions[hoveredIndex] &&
    pos.y >= originY &&
    pos.y <= originY + sizePositions[hoveredIndex]
  ) {
    return hoveredIndex;
  }

  const idx = items.findIndex((item) => {
    const x = originX + baseSize * item.column;
    return (
      pos.x >= x &&
      pos.x <= x + baseSize &&
      pos.y >= originY &&
      pos.y <= originY + baseSize
    );
  });

  return idx === -1 ? null : idx;
};
